import { useEffect, useMemo, useState, useRef } from "react";
import cn from "classnames";
import moment from "moment";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

// components
import {
  Input,
  Textarea,
  Button,
  DatePicker,
  RoundedButton,
} from "components/LibraryComponents/Components";
import { TrashOutlineIcon } from "components/icons";
import ConfirmationDialog from "./ConfirmationDialog";

// types
import { SectionsProps } from "pages/BusinessProfileEdit/BusinessProfileEdit";
import { BusinessItem } from "types/results-panel.types";

// styles
import styles from "./SizeProtestSection.module.scss";

// fetch
import { 
  createSizeDetermination, 
  updateSizeDetermination, 
  getSizeDeterminationData, 
  deleteSizeDetermination 
} from "data/fetch";

type SizeDeterminationFormData = {
  revenue_size: string;
  employee_size: string;
  // megawatt_hours: string;
  // oil_barrel: string;
  // total_assets: string;
  determination: string;
  determination_date: string;

  id?: string;
  uei?: string;
  sba_status?: string;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  deleted_at?: string;
}

function formatDate(date: string, isPreview = false) {
  if (!date) return "";

  if (isPreview) return moment(date).format("MM/DD/YYYY");

  return moment(date).format("YYYY-MM-DD");
}

function anyFilledField(formData: SizeDeterminationFormData) {
  return (
    formData.revenue_size !== '' ||
    formData.employee_size !== '' 
    // ||
    // formData.oil_barrel !== '' ||
    // formData.megawatt_hours !== '' ||
    // formData.total_assets !== ''
  );
}

export default function SizeProtestSection({
  setUnsavedChanges,
  businessProfileData
}: SectionsProps) {

  const { uei, cage_code } = useParams<{ uei: string, cage_code: string }>();

  console.log('businessProfileData', businessProfileData)

  const startingData = {
    id: '',
    revenue_size: '',
    employee_size: '',
    // oil_barrel: '',
    // megawatt_hours: '',
    // total_assets: '',
    determination_date: '',
    determination: '',
  }

  const [mode, setMode] = useState<"save" | "edit" | "delete" | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<SizeDeterminationFormData>(startingData);
  const [formData, setFormData] = useState<SizeDeterminationFormData>(startingData);
  const [isNew, setIsNew] = useState<boolean>(true);

  const isInitialized = useRef(false); // Track if formState has been initialized

  const {
    data: sizeDeterminationData,
    isPending: sizePending
  } = useQuery<
    SizeDeterminationFormData[]
  >({
    queryKey: ["size-determination", uei],

    queryFn: () => getSizeDeterminationData(uei),

    gcTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    enabled: Boolean(uei),
    retry: false,
  });

  const isPending = sizePending || !businessProfileData;

  const isDisabled =
    !anyFilledField(formData) ||
    formData.determination_date === "" ||
    formData.determination === "";

  // Initialize formState only when extendedProfile is available and formState is empty
  useEffect(() => {
    const isFormStateEmpty = Object.values(formData).every(
      (value) => value === ""
    );

    const recentDetermination = sizeDeterminationData?.length > 0 ? sizeDeterminationData[0] : null;

    const isNewDetermination = recentDetermination ? false : true
    setIsNew(isNewDetermination);
    setMode(isNewDetermination ? null : "save")

    if (sizeDeterminationData && !isInitialized.current && isFormStateEmpty) {

      const newFormData = {
        id: recentDetermination?.id || '',
        revenue_size: recentDetermination?.revenue_size ? String(recentDetermination.revenue_size) : '',
        employee_size: recentDetermination?.employee_size ? String(recentDetermination?.employee_size) : '',
        // oil_barrel: recentDetermination?.oil_barrel ? String(recentDetermination?.oil_barrel) : '',
        // megawatt_hours: recentDetermination?.megawatt_hours ? String(recentDetermination?.megawatt_hours) : '',
        // total_assets: recentDetermination?.total_assets ? String(recentDetermination?.total_assets) : '',
        determination_date:
          recentDetermination?.determination_date
            ? formatDate(recentDetermination.determination_date)
            : formatDate(new Date().toISOString()),
        determination:
          recentDetermination?.determination || '',
      };

      setInitialData(newFormData)
      setFormData(newFormData);

      isInitialized.current = true; // Mark as initialized to prevent future updates
    } 

  }, [sizeDeterminationData]);

  const unsavedChanges = 
    formData?.revenue_size !== initialData?.revenue_size ||
    formData?.employee_size !== initialData?.employee_size ||
    // formData?.oil_barrel !== initialData?.oil_barrel ||
    // formData?.megawatt_hours !== initialData?.megawatt_hours ||
    // formData?.total_assets !== initialData?.total_assets ||
    moment(formData?.determination_date).format('MM/DD/YYYY') !== moment(initialData?.determination_date).format('MM/DD/YYYY') || // 
    formData?.determination !== initialData?.determination

  useEffect(() => {
    setUnsavedChanges(unsavedChanges);
    return () => {
      setUnsavedChanges(false);
    };
  }, [unsavedChanges, setUnsavedChanges]);

  useEffect(() => { // make sure there is always a value for date
    if (formData.determination_date === '') {
      setFormData((prevFormData) => ({
        ...prevFormData,
        determination_date: formatDate(new Date().toISOString()),
      }));
    }
  }, [formData.determination_date]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleDateChange = (value: string) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      determination_date: value,
    }));
  };

  const handleSavedChanges = async () => {
    if (!unsavedChanges || isSaving) return;

    try {
      setIsSaving(true);

      const payload = {
        revenue_size: Number(formData.revenue_size),
        employee_size: Number(formData.employee_size),
        // oil_barrel: Number(formData.oil_barrel),
        // megawatt_hours: Number(formData.megawatt_hours),
        // total_assets: Number(formData.total_assets),
        determination_date: new Date(formData.determination_date),
        determination: formData.determination,
      };

      const response = isNew ? await createSizeDetermination(uei, JSON.stringify(payload)) : await updateSizeDetermination(uei, formData.id, JSON.stringify(payload));
      const data = await response.json();

      const newData = {
          ...formData,
          determination_date: moment(formData.determination_date).format('YYYY-MM-DD'),
          id: data.id,
        }

      setInitialData(newData);
      setFormData(newData)

      toast.success(`Size protest saved successfully`, { duration: Infinity });
      setMode('save')
    } catch (error) {
      console.error("Error creating size determination:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditChanges = () => {
    setMode(null);
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleHandleRemoveProtest = async () => {
    try {
      setIsDeleting(true);

      await deleteSizeDetermination(uei, formData.id);

      // Reset to initial state
      const newData = {
        ...startingData,
        determination_date: formatDate(new Date().toISOString()),
      }
      setInitialData(newData);
      setFormData(newData);
      isInitialized.current = false;

      setMode(null);
      setIsNew(true)
      setOpen(false);

      toast.success(`Size protest removed successfully`, { duration: Infinity });
    } catch (error) {
      console.error("Error removing size protest:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className={styles["size-protest-section-root"]}>
        <h2 className={styles["heading"]}>Size protest</h2>

        <div className={cn(styles["section-content"], styles["space-y-6"])}>
          <div className={styles["space-y-4"]}>
            <div className={styles["group-row"]}>
              <h3 className={styles["title"]}>Sizes from SAM</h3>
              <h3 className={styles["title"]}>
                SBA formal size determinations
              </h3>
            </div>

            <p className={cn(styles["desc"])}>
              At least one SBA formal size determination must be filled out to
              submit a size protest.
            </p>
          </div>

          {isPending && <p>Loading...</p>}
          {!isPending && (
            <>

              {!mode && (
                <SizeProtestForm
                  {...{ formData, handleInputChange, handleDateChange }}
                  profileData={businessProfileData}
                />
              )}

              {mode === "save" && <SizeProtestPreview {...{ formData }} profileData={businessProfileData} />}

              {!mode && (
                <Button
                  type="button"
                  loading={isSaving}
                  disabled={isDisabled || !unsavedChanges || isSaving}
                  onClick={handleSavedChanges}
                >
                  {isSaving ? "Saving..." : "Save changes"}
                </Button>
              )}

              {mode === "save" && (
                <div className={styles["group-btn"]}>
                  <Button
                    type="button"
                    // disabled={isDisabled}
                    onClick={handleEditChanges}
                  >
                    Edit size protest
                  </Button>

                  <RoundedButton
                    type="button"
                    variant="ghost"
                    className={styles["icon-btn"]}
                    onClick={handleOpenDialog}
                    aria-label="Remove size protest"
                  >
                    <TrashOutlineIcon aria-hidden={true} />
                    Remove size protest
                  </RoundedButton>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {open && (
        <ConfirmationDialog
          open={open}
          isLoading={isDeleting}
          setOpen={setOpen}
          handleClick={handleHandleRemoveProtest}
        />
      )}
    </>
  );
}

type SizeProtestFormProps = {
  formData: SizeDeterminationFormData;
  handleInputChange: (
    data: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleDateChange: (value: string) => void;
  profileData: BusinessItem;
};

function SizeProtestForm({
  formData,
  handleInputChange,
  handleDateChange,
  profileData
}: SizeProtestFormProps) {
  return (
    <div className={styles["space-y-6"]}>
      <div className={styles["group-row"]}>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Revenue</p>
          <p className={styles["value"]}>${profileData?.annual_revenue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "Not provided"}</p>
        </div>

        <Input
          id="revenue_size"
          name="revenue_size"
          type="number"
          label="Revenue"
          inputClassName={styles["input-number"]}
          value={formData.revenue_size}
          onChange={handleInputChange}
        />
      </div>

      <div className={styles["group-row"]}>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Employees</p>
          <p className={styles["value"]}>{profileData?.business_size.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "Not provided"}</p>
        </div>

        <Input
          id="employee_size"
          name="employee_size"
          type="number"
          label="Employees"
          placeholder="Type a number"
          value={formData.employee_size}
          onChange={handleInputChange}
        />
      </div>

      {/* <div className={styles["group-row"]}>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Barrels of oil</p>
          <p className={cn(styles["value"], styles["italic"])}>Not provided</p>
        </div>

        <Input
          id="oil_barrel"
          name="oil_barrel"
          type="number"
          label="Barrels of oil"
          placeholder="Type a number"
          value={formData.oil_barrel}
          onChange={handleInputChange}
        />
      </div>

      <div className={styles["group-row"]}>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Megawatt hours</p>
          <p className={cn(styles["value"], styles["italic"])}>Not provided</p>
        </div>

        <Input
          id="megawatt_hours"
          name="megawatt_hours"
          type="number"
          label="Megawatt hours"
          placeholder="Type a number"
          value={formData.megawatt_hours}
          onChange={handleInputChange}
        />
      </div>

      <div className={styles["group-row"]}>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Total assets</p>
          <p className={cn(styles["value"], styles["italic"])}>Not provided</p>
        </div>

        <Input
          id="total_assets"
          name="total_assets"
          type="number"
          label="Total assets"
          placeholder="Type a number"
          value={formData.total_assets}
          onChange={handleInputChange}
        />
      </div> */}

      <hr className={styles["divider"]} />

      <p className={styles["desc"]}>
        A red asterisk (<span className={styles["asterik"]}>*</span>) indicates
        a required field.
      </p>

      {formData.determination_date && ( // wait until date is set to initialize
        <div className={cn(styles["group-row"], styles["sm-group-full"])}>
          <DatePicker
            id="determination_date"
            name="determination_date"
            label="SBA Size Determination Date *"
            defaultValue={moment(formData.determination_date).format('YYYY-MM-DD')} // requires format YYYY-MM-DD to initialize
            onChange={handleDateChange}
          />
          <div />
        </div>
      )}

      <Textarea
        id="determination"
        name="determination"
        label="SBA Formal Size Determination Reason(s) *"
        placeholder="SBA Formal Size Determination Reason(s)"
        value={formData.determination}
        onChange={handleInputChange}
      />
    </div>
  );
}

function SizeProtestPreview({ formData, profileData }: { formData: SizeDeterminationFormData, profileData: BusinessItem }) {
  return (
    <div className={styles["space-y-6"]}>
      <div className={styles["group-row"]}>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Revenue</p>
          <p className={styles["value"]}>$8,966,697</p>
        </div>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Revenue</p>
          <p className={styles["value"]}>
            {formData?.revenue_size || <i>Not provided</i>}
          </p>
        </div>
      </div>

      <div className={styles["group-row"]}>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Employees</p>
          <p className={styles["value"]}>{profileData.business_size}</p>
        </div>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Employees</p>
          <p className={styles["value"]}>
            {formData?.employee_size || <i>Not provided</i>}
          </p>
        </div>
      </div>

      {/* <div className={styles["group-row"]}>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Barrels of oil</p>
          <p className={cn(styles["value"], styles["italic"])}>Not provided</p>
        </div>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Barrels of oil</p>
          <p
            className={cn(styles["value"], {
              [styles["italic"]]: !formData.oil_barrel,
            })}
          >
            {formData.oil_barrel
              ? Number(formData.oil_barrel).toLocaleString("en-US")
              : "Not provided"}
          </p>
        </div>
      </div> */}

      {/* <div className={styles["group-row"]}>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Megawatt hours</p>
          <p className={cn(styles["value"], styles["italic"])}>Not provided</p>
        </div>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Megawatt hours</p>
          <p
            className={cn(styles["value"], {
              [styles["italic"]]: !formData.megawatt_hours,
            })}
          >
            {formData.megawatt_hours
              ? Number(formData.megawatt_hours).toLocaleString("en-US")
              : "Not provided"}
          </p>
        </div>
      </div> */}

      {/* <div className={styles["group-row"]}>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Total assets</p>
          <p className={cn(styles["value"], styles["italic"])}>Not provided</p>
        </div>
        <div className={styles["group"]}>
          <p className={styles["label"]}>Total assets</p>
          <p
            className={cn(styles["value"], {
              [styles["italic"]]: !formData.total_assets,
            })}
          >
            {formData.total_assets
              ? Number(formData.total_assets).toLocaleString("en-US")
              : "Not provided"}
          </p>
        </div>
      </div> */}

      <hr className={styles["divider"]} />

      <div className={styles["group-row"]}>
        <div className={styles["group"]}>
          <p className={styles["label"]}>SBA Size Determination Date</p>
          <p className={styles["value"]}>
            {formatDate(formData.determination_date, true)}
          </p>
        </div>
      </div>

      <div className={styles["group"]}>
        <p className={styles["label"]}>
          SBA Formal Size Determination Reason(s)
        </p>
        <p className={styles["value"]}>{formData.determination}</p>
      </div>
    </div>
  );
}
